import React, { memo, useEffect, useMemo, useRef } from 'react';

// Casino-style penalty scene drawn in a fixed 1600x1000 coordinate space:
// a lit stadium in perspective, a silhouette keeper with dive poses and an
// animated ball. Purely presentational; the outcome comes from props.

// Goal mouth (inside of the frame) and zone geometry: 3 columns x 2 rows
const GOAL = { left: 380, right: 1220, top: 360, bottom: 640 };
const BACK = { left: 432, right: 1168, top: 402, bottom: 612 };
const COL_W = (GOAL.right - GOAL.left) / 3;
const ROW_H = (GOAL.bottom - GOAL.top) / 2;
const SPOT = { x: 800, y: 905 };
const PITCH_TOP = 520;

export function zoneRect(zone) {
  const col = zone % 3;
  const row = Math.floor(zone / 3);
  return { x: GOAL.left + col * COL_W, y: GOAL.top + row * ROW_H, w: COL_W, h: ROW_H, col, row };
}

function zoneCenter(zone) {
  const r = zoneRect(zone);
  return { x: r.x + r.w / 2, y: r.y + r.h / 2 + (r.row === 0 ? -6 : 8) };
}

/* ---------------------------------------------------------------------------
   Keeper: joints relative to the pelvis (y down), drawn as tapered limbs so
   the silhouette reads as a person rather than a stick figure.
--------------------------------------------------------------------------- */
const POSES = {
  ready: {
    head: [0, -100], neck: [0, -78],
    sh: [[-26, -70], [26, -70]], el: [[-50, -44], [50, -44]], ha: [[-58, -14], [58, -14]],
    hip: [[-12, 2], [12, 2]], kn: [[-31, 44], [31, 44]], ft: [[-27, 94], [27, 94]],
  },
  stretch: {
    head: [0, -104], neck: [0, -82],
    sh: [[-21, -74], [21, -74]], el: [[-27, -116], [23, -116]], ha: [[-25, -156], [29, -156]],
    hip: [[-10, 2], [10, 2]], kn: [[-18, 50], [15, 48]], ft: [[-30, 96], [6, 94]],
  },
  jump: {
    head: [0, -104], neck: [0, -82],
    sh: [[-22, -74], [22, -74]], el: [[-21, -118], [21, -118]], ha: [[-24, -158], [24, -158]],
    hip: [[-10, 2], [10, 2]], kn: [[-13, 46], [13, 46]], ft: [[-9, 90], [9, 90]],
  },
  low: {
    head: [0, -86], neck: [0, -64],
    sh: [[-26, -56], [26, -56]], el: [[-50, -26], [50, -26]], ha: [[-46, 8], [46, 8]],
    hip: [[-13, 0], [13, 0]], kn: [[-40, 32], [40, 32]], ft: [[-34, 70], [34, 70]],
  },
};

const KEEPER_SCALE = 1.15;
const KEEPER_IDLE = { x: 800, y: GOAL.bottom - 96 * KEEPER_SCALE, rot: 0, pose: 'ready', air: 0 };

function keeperFor(zone) {
  const { col, row } = zoneRect(zone);
  if (col === 1) {
    return row === 0
      ? { x: 800, y: 528, rot: 0, pose: 'jump', air: 0.8 }
      : { x: 800, y: GOAL.bottom - 70 * KEEPER_SCALE, rot: 0, pose: 'low', air: 0 };
  }
  const side = col === 0 ? -1 : 1;
  return row === 0
    ? { x: 800 + side * 165, y: 478, rot: side * 60, pose: 'stretch', air: 1 }
    : { x: 800 + side * 150, y: 586, rot: side * 82, pose: 'stretch', air: 0.4 };
}

const OUTLINE = '#0b0b10';
const FACE_URL = '/avatar/indiano.png';
// Avatar is drawn bigger than life (mascot style) so the face reads at game size
const FACE_SIZE = 74;
// Chin sits at 84% of the avatar image height
const FACE_CHIN = 0.84;

const along = (a, b, t) => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
const angle = (a, b) => (Math.atan2(b[1] - a[1], b[0] - a[0]) * 180) / Math.PI;

const OUTLINE_W = 6;
const line = (...pts) => `M${pts.map(q => q.join(',')).join(' L')}`;

/* Limbs are single stroked paths through the joints (round caps and joins),
   so elbows and knees bend smoothly with no seams. All kit colours use
   gradients in the keeper's own coordinates, so shading is continuous from
   sleeve to torso. Outline pass: same shapes, wider, in the outline colour. */
function Stroke({ d, w, color, outline, cap = 'round' }) {
  return (
    <path
      d={d}
      fill="none"
      stroke={outline ? OUTLINE : color}
      strokeWidth={outline ? w + OUTLINE_W : w}
      strokeLinecap={cap}
      strokeLinejoin="round"
    />
  );
}

function Fill({ d, color, outline }) {
  return outline
    ? <path d={d} fill={OUTLINE} stroke={OUTLINE} strokeWidth={OUTLINE_W} strokeLinejoin="round" />
    : <path d={d} fill={color} />;
}

// Goalkeeper glove: palm, four spread fingers and a thumb, along the forearm
function Glove({ hand, elbow, side, outline }) {
  const color = outline ? OUTLINE : '#f4f4f5';
  const grow = outline ? OUTLINE_W : 0;
  const rot = angle(elbow, hand);
  const thumbY = side < 0 ? 11 : -11;
  return (
    <g transform={`translate(${hand[0]} ${hand[1]}) rotate(${rot})`} stroke={color} strokeLinecap="round" fill={color}>
      {[-8.5, -2.8, 2.8, 8.5].map((y, k) => (
        <line key={k} x1="12" y1={y * 0.8} x2={23.5 - Math.abs(y) * 0.45} y2={y * 1.2} strokeWidth={6.2 + grow} />
      ))}
      <line x1="5" y1={thumbY * 0.55} x2="12" y2={thumbY * 1.4} strokeWidth={6.8 + grow} />
      <ellipse cx="7" cy="0" rx={11.5 + grow / 2} ry={12.5 + grow / 2} stroke="none" />
      {!outline && (
        <>
          <ellipse cx="9" cy="1" rx="6.5" ry="8" fill="#d4d4d8" stroke="none" opacity="0.6" />
          <rect x="-5" y="-11" width="7" height="22" rx="3" fill="#3b0f7a" stroke="none" />
        </>
      )}
    </g>
  );
}

function bodyParts(p, outline) {
  const o = outline ? 2 : 0;
  const [shL, shR] = p.sh;
  const [hipL, hipR] = p.hip;
  const midSh = along(shL, shR, 0.5);
  const midHip = along(hipL, hipR, 0.5);

  // Torso: rounded shoulders, chest, tapering to the waist
  const torso = `M${shL[0] - 9},${shL[1] + 3}
    Q${shL[0] - 8},${shL[1] - 11} ${midSh[0]},${midSh[1] - 10}
    Q${shR[0] + 8},${shR[1] - 11} ${shR[0] + 9},${shR[1] + 3}
    Q${shR[0] + 6},${(shR[1] + hipR[1]) / 2 - 4} ${hipR[0] + 9},${hipR[1] - 5}
    L${hipL[0] - 9},${hipL[1] - 5}
    Q${shL[0] - 6},${(shL[1] + hipL[1]) / 2 - 4} ${shL[0] - 9},${shL[1] + 3} Z`;

  const shorts = `M${hipL[0] - 12},${hipL[1] - 10}
    L${hipR[0] + 12},${hipR[1] - 10}
    L${hipR[0] + 16},${hipR[1] + 18}
    Q${midHip[0] + 5},${midHip[1] + 15} ${midHip[0]},${midHip[1] + 9}
    Q${midHip[0] - 5},${midHip[1] + 15} ${hipL[0] - 16},${hipL[1] + 18} Z`;

  return (
    <g>
      {[0, 1].map(i => {
        const [hip, knee, foot] = [p.hip[i], p.kn[i], p.ft[i]];
        const dir = i === 0 ? -1 : 1;
        const sockTop = along(knee, foot, 0.1);
        const boot = `M${foot[0] - 9 * dir},${foot[1] - 5}
            Q${foot[0] + 5 * dir},${foot[1] - 9} ${foot[0] + 16 * dir},${foot[1] + 1}
            Q${foot[0] + 18 * dir},${foot[1] + 8} ${foot[0] + 5 * dir},${foot[1] + 8}
            L${foot[0] - 11 * dir},${foot[1] + 7} Z`;
        return (
          <g key={`leg${i}`}>
            {/* thigh is thicker; one path hip -> knee -> ankle keeps the knee smooth */}
            <Stroke d={line(hip, knee)} w={21} color="url(#pk-tights)" outline={outline} />
            <Stroke d={line(hip, knee, foot)} w={15.5} color="url(#pk-tights)" outline={outline} />
            {!outline && <Stroke d={line(sockTop, foot)} w={15} color="url(#pk-sock)" />}
            {!outline && <Stroke d={line(along(knee, foot, 0.2), along(knee, foot, 0.3))} w={15.4} color="#f4f4f5" cap="butt" />}
            <Fill d={boot} color="url(#pk-boot)" outline={outline} />
          </g>
        );
      })}

      <Fill d={shorts} color="url(#pk-shorts)" outline={outline} />
      <Fill d={torso} color="url(#pk-kit)" outline={outline} />

      {[0, 1].map(i => {
        const [sh, el, ha] = [p.sh[i], p.el[i], p.ha[i]];
        return (
          <g key={`arm${i}`}>
            <Stroke d={line(sh, el)} w={18} color="url(#pk-kit)" outline={outline} />
            <Stroke d={line(sh, el, ha)} w={14} color="url(#pk-kit)" outline={outline} />
            {!outline && <Stroke d={line(along(el, ha, 0.68), along(el, ha, 0.86))} w={14.6} color="#3b0f7a" cap="butt" />}
            <Glove hand={ha} elbow={el} side={i === 0 ? -1 : 1} outline={outline} />
          </g>
        );
      })}

      <Stroke d={line(p.neck, [p.neck[0], p.neck[1] - 8 - o])} w={13} color="#5a3a24" outline={outline} />
    </g>
  );
}

const KeeperBody = memo(function KeeperBody({ pose }) {
  const p = POSES[pose];
  const [shL, shR] = p.sh;
  const [hipL, hipR] = p.hip;
  const midSh = along(shL, shR, 0.5);
  const midHip = along(hipL, hipR, 0.5);
  const chest = along(midSh, midHip, 0.38);
  const faceX = p.neck[0] - FACE_SIZE / 2;
  const faceY = p.neck[1] - 6 - FACE_SIZE * FACE_CHIN;

  return (
    <g>
      {bodyParts(p, true)}
      {bodyParts(p, false)}

      {/* Jersey: collar, shading on the right side, creases, number */}
      <path d={`M${shL[0] + 9},${shL[1] - 6} Q${midSh[0]},${midSh[1] + 8} ${shR[0] - 9},${shR[1] - 6}`} stroke="#2e0b63" strokeWidth="4" fill="none" strokeLinecap="round" />
      <path
        d={`M${shR[0] + 4},${shR[1] + 6} Q${shR[0] + 2},${(shR[1] + hipR[1]) / 2} ${hipR[0] + 5},${hipR[1] - 8}`}
        stroke="#2e0b63" strokeOpacity="0.45" strokeWidth="8" fill="none" strokeLinecap="round"
      />
      <path
        d={`M${midHip[0] - 12},${midHip[1] - 14} Q${midHip[0] - 2},${midHip[1] - 20} ${midHip[0] + 12},${midHip[1] - 13}`}
        stroke="#2e0b63" strokeOpacity="0.35" strokeWidth="2" fill="none" strokeLinecap="round"
      />
      <path
        d={`M${shL[0] - 2},${shL[1] + 8} Q${shL[0] + 2},${(shL[1] + hipL[1]) / 2} ${hipL[0] - 1},${hipL[1] - 12}`}
        stroke="#ffffff" strokeOpacity="0.16" strokeWidth="5" fill="none" strokeLinecap="round"
      />
      <text
        x={chest[0]} y={chest[1] + 6}
        textAnchor="middle"
        className="pk-keeper-number"
        transform={`rotate(0 ${chest[0]} ${chest[1]})`}
      >1</text>

      {[0, 1].map(i => (
        <g key={`detail${i}`}>
          {/* shorts side stripe */}
          <line
            x1={p.hip[i][0] + (i ? 13 : -13)} y1={p.hip[i][1] - 8}
            x2={p.hip[i][0] + (i ? 17 : -17)} y2={p.hip[i][1] + 16}
            stroke="#7c3aed" strokeWidth="3" strokeLinecap="round"
          />
          {/* boot studs */}
          <rect x={p.ft[i][0] - 6} y={p.ft[i][1] + 7} width="12" height="2.5" rx="1" fill="#f4f4f5" opacity="0.7" />
        </g>
      ))}

      {/* Face: the avatar image, clipped to drop its collar */}
      <image
        href={FACE_URL}
        x={faceX}
        y={faceY}
        width={FACE_SIZE}
        height={FACE_SIZE}
        clipPath="url(#pk-face-clip)"
        preserveAspectRatio="xMidYMid meet"
      />
    </g>
  );
});

/* ---------------------------------------------------------------------------
   Static stadium
--------------------------------------------------------------------------- */
function seeded(seed) {
  let s = seed;
  return () => {
    s = (s * 16807) % 2147483647;
    return (s - 1) / 2147483646;
  };
}

const Stadium = memo(function Stadium() {
  const crowd = useMemo(() => {
    const rand = seeded(7);
    const palette = ['#262a35', '#30343f', '#3b3f4b', '#1f222b', '#6d28d9', '#e4e4e7', '#7f1d1d'];
    const dots = [];
    for (let row = 0; row < 15; row += 1) {
      const y = 70 + row * 26 + rand() * 6;
      const size = 3 + row * 0.55;
      for (let x = -10; x < 1610; x += size * 2.4 + rand() * 6) {
        const c = rand();
        dots.push({
          x: x + rand() * 4,
          y: y + rand() * 8,
          r: size * (0.75 + rand() * 0.4),
          fill: c < 0.05 ? palette[4] : c < 0.08 ? palette[5] : c < 0.1 ? palette[6] : palette[Math.floor(rand() * 4)],
          o: 0.35 + rand() * 0.5,
        });
      }
    }
    return dots;
  }, []);

  // Mowing stripes converging towards the goal
  const stripes = useMemo(() => {
    const vp = { x: 800, y: 250 };
    const at = (xb, y) => vp.x + (xb - vp.x) * (y - vp.y) / (1000 - vp.y);
    const list = [];
    for (let i = -12; i < 12; i += 1) {
      const x0 = 800 + i * 210;
      const x1 = x0 + 210;
      list.push({
        points: `${at(x0, PITCH_TOP)},${PITCH_TOP} ${at(x1, PITCH_TOP)},${PITCH_TOP} ${x1},1000 ${x0},1000`,
        dark: i % 2 === 0,
      });
    }
    return list;
  }, []);

  return (
    <g>
      <rect x="0" y="0" width="1600" height={PITCH_TOP} fill="url(#pk-stands)" />
      <g filter="url(#pk-blur-crowd)">
        {crowd.map((d, i) => (
          <circle key={i} cx={d.x} cy={d.y} r={d.r} fill={d.fill} fillOpacity={d.o} />
        ))}
      </g>
      <rect x="0" y="0" width="1600" height={PITCH_TOP} fill="url(#pk-stands-fade)" />

      {/* Floodlights */}
      {[180, 1420].map(x => (
        <g key={x}>
          <ellipse cx={x} cy="20" rx="520" ry="300" fill="url(#pk-flood)" />
          {[0, 1, 2, 3, 4].map(i => (
            <circle key={i} cx={x - 40 + i * 20} cy="28" r="4.5" fill="#ffffff" fillOpacity="0.9" filter="url(#pk-glow)" />
          ))}
        </g>
      ))}

      {/* LED board behind the goal */}
      <rect x="0" y="470" width="1600" height="50" fill="url(#pk-board)" />
      <rect x="0" y="470" width="1600" height="1.5" fill="#ffffff" fillOpacity="0.12" />
      <text x="800" y="503" textAnchor="middle" className="pk-led">
        JIZONN · PENÁLTIS · JIZONN · PENÁLTIS · JIZONN · PENÁLTIS
      </text>

      {/* Pitch */}
      <rect x="0" y={PITCH_TOP} width="1600" height={1000 - PITCH_TOP} fill="#14381f" />
      {stripes.map((s, i) => (
        <polygon key={i} points={s.points} fill={s.dark ? '#123319' : '#1a4426'} />
      ))}
      <rect x="0" y={PITCH_TOP} width="1600" height={1000 - PITCH_TOP} fill="url(#pk-pitch-light)" />
      <rect x="0" y={PITCH_TOP} width="1600" height="40" fill="url(#pk-pitch-edge)" />

      {/* Markings */}
      <g stroke="#ffffff" strokeOpacity="0.75" fill="none" strokeLinecap="round">
        <line x1="-20" y1={GOAL.bottom} x2="1620" y2={GOAL.bottom} strokeWidth="4" />
        <line x1="-20" y1="716" x2="1620" y2="716" strokeWidth="5" />
      </g>
      <ellipse cx={SPOT.x} cy={SPOT.y + 18} rx="15" ry="6" fill="#ffffff" fillOpacity="0.8" />

      {/* Post shadows on the grass (light from above-right) */}
      <g fill="#000" fillOpacity="0.22">
        <polygon points={`${GOAL.left - 8},${GOAL.bottom} ${GOAL.left + 8},${GOAL.bottom} ${GOAL.left - 70},${GOAL.bottom + 60} ${GOAL.left - 86},${GOAL.bottom + 60}`} />
        <polygon points={`${GOAL.right - 8},${GOAL.bottom} ${GOAL.right + 8},${GOAL.bottom} ${GOAL.right - 70},${GOAL.bottom + 60} ${GOAL.right - 86},${GOAL.bottom + 60}`} />
      </g>
    </g>
  );
});

const Net = memo(function Net({ hit }) {
  return (
    <g className={`pk-net ${hit ? 'is-hit' : ''}`}>
      <polygon points={`${BACK.left},${BACK.top} ${BACK.right},${BACK.top} ${BACK.right},${BACK.bottom} ${BACK.left},${BACK.bottom}`} fill="#05070a" fillOpacity="0.55" />
      <polygon points={`${GOAL.left},${GOAL.top} ${BACK.left},${BACK.top} ${BACK.left},${BACK.bottom} ${GOAL.left},${GOAL.bottom}`} fill="#05070a" fillOpacity="0.35" />
      <polygon points={`${GOAL.right},${GOAL.top} ${BACK.right},${BACK.top} ${BACK.right},${BACK.bottom} ${GOAL.right},${GOAL.bottom}`} fill="#05070a" fillOpacity="0.35" />
      <polygon points={`${GOAL.left},${GOAL.top} ${GOAL.right},${GOAL.top} ${BACK.right},${BACK.top} ${BACK.left},${BACK.top}`} fill="#05070a" fillOpacity="0.3" />
      <polygon points={`${GOAL.left},${GOAL.top} ${GOAL.right},${GOAL.top} ${GOAL.right},${GOAL.bottom} ${GOAL.left},${GOAL.bottom}`} fill="url(#pk-mesh)" />
      <g stroke="#d4d4d8" strokeOpacity="0.5" strokeWidth="3" fill="none">
        <polyline points={`${BACK.left},${BACK.bottom} ${BACK.left},${BACK.top} ${BACK.right},${BACK.top} ${BACK.right},${BACK.bottom}`} />
        <line x1={GOAL.left} y1={GOAL.top} x2={BACK.left} y2={BACK.top} />
        <line x1={GOAL.right} y1={GOAL.top} x2={BACK.right} y2={BACK.top} />
        <line x1={GOAL.left} y1={GOAL.bottom} x2={BACK.left} y2={BACK.bottom} />
        <line x1={GOAL.right} y1={GOAL.bottom} x2={BACK.right} y2={BACK.bottom} />
      </g>
    </g>
  );
});

const Frame = memo(function Frame() {
  return (
    <g>
      <rect x={GOAL.left - 16} y={GOAL.top - 16} width="16" height={GOAL.bottom - GOAL.top + 18} rx="6" fill="url(#pk-post)" />
      <rect x={GOAL.right} y={GOAL.top - 16} width="16" height={GOAL.bottom - GOAL.top + 18} rx="6" fill="url(#pk-post)" />
      <rect x={GOAL.left - 16} y={GOAL.top - 16} width={GOAL.right - GOAL.left + 32} height="16" rx="6" fill="url(#pk-bar)" />
    </g>
  );
});

/* ---------------------------------------------------------------------------
   Ball: animated with requestAnimationFrame along an arc, then either sinks
   into the net (goal) or is parried away (save).
--------------------------------------------------------------------------- */
const FLIGHT = 520;
const AFTER = 420;

function easeOutCubic(t) {
  return 1 - (1 - t) ** 3;
}

function useBallAnimation(shot, ballRef, shadowRef) {
  useEffect(() => {
    const ball = ballRef.current;
    const shadow = shadowRef.current;
    if (!ball || !shadow) return undefined;

    const place = (x, y, s, rot, sx, sy, ss, so) => {
      ball.setAttribute('transform', `translate(${x} ${y}) scale(${s}) rotate(${rot})`);
      shadow.setAttribute('transform', `translate(${sx} ${sy}) scale(${ss})`);
      shadow.setAttribute('fill-opacity', so);
    };

    if (!shot) {
      place(SPOT.x, SPOT.y, 1, 0, SPOT.x, SPOT.y + 22, 1, 0.45);
      return undefined;
    }

    const target = zoneCenter(shot.zone);
    const ctrl = { x: (SPOT.x + target.x) / 2, y: Math.min(SPOT.y, target.y) - 70 };
    const high = zoneRect(shot.zone).row === 0;
    const side = target.x < 800 ? -1 : target.x > 800 ? 1 : (shot.keeperZone % 3 === 0 ? 1 : -1);
    const parry = { x: target.x + side * 170, y: target.y + 210 };
    let frame;
    const t0 = performance.now();

    const tick = (now) => {
      const el = now - t0;
      if (el <= FLIGHT) {
        const t = easeOutCubic(el / FLIGHT);
        const u = 1 - t;
        const x = u * u * SPOT.x + 2 * u * t * ctrl.x + t * t * target.x;
        const y = u * u * SPOT.y + 2 * u * t * ctrl.y + t * t * target.y;
        const s = 1 - 0.48 * t;
        place(x, y, s, 900 * t, x, SPOT.y + 22 + (GOAL.bottom + 8 - SPOT.y - 22) * t, 1 - 0.55 * t, 0.45 - (high ? 0.3 : 0.2) * t);
      } else {
        const t = Math.min(1, easeOutCubic((el - FLIGHT) / AFTER));
        if (shot.goal) {
          // Sink a little into the net
          const y = target.y + 14 * t;
          place(target.x, y, 0.52 - 0.06 * t, 900 + 120 * t, target.x, GOAL.bottom + 8, 0.45, 0.2);
        } else {
          const x = target.x + (parry.x - target.x) * t;
          const y = target.y + (parry.y - target.y) * t;
          place(x, y, 0.52 + 0.12 * t, 900 + 400 * t, x, GOAL.bottom + 8 + 60 * t, 0.45 + 0.2 * t, 0.2 + 0.15 * t);
        }
        if (el >= FLIGHT + AFTER) return;
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [shot, ballRef, shadowRef]);
}

function Ball({ shot }) {
  const ballRef = useRef(null);
  const shadowRef = useRef(null);
  useBallAnimation(shot, ballRef, shadowRef);
  return (
    <>
      <ellipse ref={shadowRef} rx="28" ry="8" fill="#000" fillOpacity="0.45" transform={`translate(${SPOT.x} ${SPOT.y + 22})`} />
      <g ref={ballRef} transform={`translate(${SPOT.x} ${SPOT.y})`}>
        <circle r="24" fill="url(#pk-ball)" />
        <g fill="#1f1f24">
          <path d="M0,-8 L7.6,-2.5 L4.7,6.5 L-4.7,6.5 L-7.6,-2.5 Z" />
          <path d="M0,-24 L-6,-19.5 L-3,-14 L3,-14 L6,-19.5 Z" opacity="0.85" />
          <path d="M22.8,-7.4 L16,-11 L13.3,-4.3 L17.3,1.2 L23.5,-1.5 Z" opacity="0.8" />
          <path d="M-22.8,-7.4 L-16,-11 L-13.3,-4.3 L-17.3,1.2 L-23.5,-1.5 Z" opacity="0.8" />
          <path d="M14.1,19.4 L15.6,12.5 L8.6,11.2 L6,17.6 L10.4,22.2 Z" opacity="0.75" />
          <path d="M-14.1,19.4 L-15.6,12.5 L-8.6,11.2 L-6,17.6 L-10.4,22.2 Z" opacity="0.75" />
        </g>
        <circle r="24" fill="url(#pk-ball-shade)" />
      </g>
    </>
  );
}

/* ---------------------------------------------------------------------------
   Scene
--------------------------------------------------------------------------- */
function Defs() {
  return (
    <defs>
      <linearGradient id="pk-stands" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#05060a" />
        <stop offset="1" stopColor="#0d1018" />
      </linearGradient>
      <linearGradient id="pk-stands-fade" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#05060a" stopOpacity="0.85" />
        <stop offset="0.45" stopColor="#05060a" stopOpacity="0.15" />
        <stop offset="1" stopColor="#05060a" stopOpacity="0.55" />
      </linearGradient>
      <radialGradient id="pk-flood" cx="0.5" cy="0.5" r="0.5">
        <stop offset="0" stopColor="#dbeafe" stopOpacity="0.22" />
        <stop offset="1" stopColor="#dbeafe" stopOpacity="0" />
      </radialGradient>
      <linearGradient id="pk-board" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#14111f" />
        <stop offset="1" stopColor="#0a0910" />
      </linearGradient>
      <radialGradient id="pk-pitch-light" cx="0.5" cy="0.35" r="0.75">
        <stop offset="0" stopColor="#ffffff" stopOpacity="0.1" />
        <stop offset="0.6" stopColor="#000000" stopOpacity="0" />
        <stop offset="1" stopColor="#000000" stopOpacity="0.45" />
      </radialGradient>
      <linearGradient id="pk-pitch-edge" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#000" stopOpacity="0.5" />
        <stop offset="1" stopColor="#000" stopOpacity="0" />
      </linearGradient>
      <pattern id="pk-mesh" width="22" height="19" patternUnits="userSpaceOnUse">
        <path d="M0,9.5 L5.5,0 L16.5,0 L22,9.5 L16.5,19 L5.5,19 Z" fill="none" stroke="#ffffff" strokeOpacity="0.2" strokeWidth="1.3" />
      </pattern>
      <linearGradient id="pk-post" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0" stopColor="#a1a1aa" />
        <stop offset="0.4" stopColor="#ffffff" />
        <stop offset="1" stopColor="#c4c4cc" />
      </linearGradient>
      <linearGradient id="pk-bar" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#ffffff" />
        <stop offset="1" stopColor="#a1a1aa" />
      </linearGradient>
      <linearGradient id="pk-kit" x1="-70" y1="-110" x2="70" y2="10" gradientUnits="userSpaceOnUse">
        <stop offset="0" stopColor="#b49bff" />
        <stop offset="0.45" stopColor="#8b5cf6" />
        <stop offset="1" stopColor="#5b21b6" />
      </linearGradient>
      <linearGradient id="pk-sock" x1="-40" y1="0" x2="40" y2="0" gradientUnits="userSpaceOnUse">
        <stop offset="0" stopColor="#8b5cf6" />
        <stop offset="1" stopColor="#5b21b6" />
      </linearGradient>
      <linearGradient id="pk-tights" x1="-40" y1="0" x2="40" y2="0" gradientUnits="userSpaceOnUse">
        <stop offset="0" stopColor="#2c2c36" />
        <stop offset="1" stopColor="#121217" />
      </linearGradient>
      <linearGradient id="pk-shorts" x1="-30" y1="-10" x2="30" y2="20" gradientUnits="userSpaceOnUse">
        <stop offset="0" stopColor="#2a2a33" />
        <stop offset="1" stopColor="#101014" />
      </linearGradient>
      <linearGradient id="pk-boot" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#2a2a30" />
        <stop offset="1" stopColor="#09090b" />
      </linearGradient>
      <radialGradient id="pk-ball" cx="0.38" cy="0.32" r="0.75">
        <stop offset="0" stopColor="#ffffff" />
        <stop offset="0.7" stopColor="#e4e4e7" />
        <stop offset="1" stopColor="#a1a1aa" />
      </radialGradient>
      <radialGradient id="pk-ball-shade" cx="0.35" cy="0.3" r="0.8">
        <stop offset="0.55" stopColor="#000" stopOpacity="0" />
        <stop offset="1" stopColor="#000" stopOpacity="0.4" />
      </radialGradient>
      <radialGradient id="pk-vignette" cx="0.5" cy="0.5" r="0.72">
        <stop offset="0.6" stopColor="#000" stopOpacity="0" />
        <stop offset="1" stopColor="#000" stopOpacity="0.55" />
      </radialGradient>
      <filter id="pk-blur-crowd" x="-2%" y="-2%" width="104%" height="104%">
        <feGaussianBlur stdDeviation="2.8" />
      </filter>
      <filter id="pk-glow" x="-200%" y="-200%" width="500%" height="500%">
        <feGaussianBlur stdDeviation="4" result="b" />
        <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
      </filter>
      <clipPath id="pk-face-clip" clipPathUnits="objectBoundingBox">
        <ellipse cx="0.5" cy="0.44" rx="0.37" ry="0.4" />
      </clipPath>
      <filter id="pk-keeper-shadow" x="-30%" y="-30%" width="160%" height="160%">
        <feDropShadow dx="0" dy="4" stdDeviation="5" floodColor="#000" floodOpacity="0.45" />
      </filter>
    </defs>
  );
}

function Targets({ hoverZone }) {
  return (
    <g className="pk-targets">
      {[0, 1, 2, 3, 4, 5].map(zone => {
        const c = zoneCenter(zone);
        const active = zone === hoverZone;
        return (
          <g key={zone} transform={`translate(${c.x} ${c.y})`} className={`pk-target ${active ? 'is-active' : ''}`}>
            <circle r="34" className="pk-target-ring" />
            <circle r="5" className="pk-target-dot" />
            {active && <path d="M-50,0 H-40 M40,0 H50 M0,-50 V-40 M0,40 V50" className="pk-target-ticks" />}
          </g>
        );
      })}
    </g>
  );
}

function PenaltyScene({ shot, showResult, viewBox, hoverZone, aiming }) {
  const keeper = shot ? keeperFor(shot.keeperZone) : KEEPER_IDLE;
  const goalHit = Boolean(shot && shot.goal && showResult);
  const impact = shot ? zoneCenter(shot.zone) : null;

  return (
    <svg
      className="pk-scene"
      viewBox={`${viewBox.x} ${viewBox.y} ${viewBox.w} ${viewBox.h}`}
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
    >
      <Defs />
      <Stadium />
      <Net hit={goalHit} />

      {/* Keeper ground shadow stays on the grass */}
      <ellipse
        className="pk-keeper-ground"
        cx="0"
        cy="0"
        rx="70"
        ry="12"
        style={{ transform: `translate(${keeper.x}px, ${GOAL.bottom + 4}px) scale(${1 - 0.45 * keeper.air})`, opacity: 0.5 - 0.25 * keeper.air }}
      />

      <Frame />

      <g
        className={`pk-keeper ${shot ? 'is-diving' : ''}`}
        style={{ transform: `translate(${keeper.x}px, ${keeper.y}px) rotate(${keeper.rot}deg) scale(${KEEPER_SCALE})` }}
        filter="url(#pk-keeper-shadow)"
      >
        <g className={aiming && !shot ? 'pk-keeper-sway' : ''}>
          <KeeperBody pose={keeper.pose} />
        </g>
      </g>

      {goalHit && impact && (
        <g transform={`translate(${impact.x} ${impact.y})`} className="pk-ripple">
          <circle r="30" />
          <circle r="30" className="pk-ripple-2" />
        </g>
      )}

      <Ball shot={shot} />

      {aiming && !shot && <Targets hoverZone={hoverZone} />}

      <rect x="0" y="0" width="1600" height="1000" fill="url(#pk-vignette)" pointerEvents="none" />
    </svg>
  );
}

export default memo(PenaltyScene);
